/**
 * Key Points Onyx Section
 */

class KeyPointsOnyx extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.items = this.querySelectorAll('.key-points-onyx__item');

    // Add to cart functionality
    this.productId = this.dataset.productId;
    this.button = this.querySelector('[data-add-to-cart]');
    this.priceElement = this.querySelector('.key-points-onyx__price');
    this.stockBadge = this.querySelector('.key-points-onyx__stock-badge');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.preorderLabel = this.querySelector('[data-preorder-ships]');

    // Get product JSON data
    const productJsonElement = this.querySelector('.key-points-onyx-product-json');
    if (productJsonElement) {
      try {
        this.productData = JSON.parse(productJsonElement.textContent);
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    }

    // Variant selection elements
    this.mainVariantSelect = this.querySelector('[data-onyx-option-main]');
    this.optionSelects = this.querySelectorAll('[data-onyx-option-select]');

    this.state = {
      id: this.dataset.variantId || null,
      options: []
    };

    this.initVariantSelection();

    if (this.button) {
      this.button.addEventListener('click', this.handleAddToCart.bind(this));
    }

    if (this.preorderLabel) {
      this.updatePreorderDate();
    }
  }

  async handleAddToCart(event) {
    event.preventDefault();

    if (!this.button || !this.state.id) return;
    if (this.button.classList.contains('is-loading') || this.button.disabled) return;

    this.button.classList.add('is-loading');
    this.button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: this.state.id,
          quantity: 1
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      if (data.status) {
        console.error('Add to cart error:', data.description);
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      if (this.cartDrawer) {
        this.cartDrawer.open(cart);
        this.cartDrawer.setActiveElement(this.button);
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart }
        }));
      } else {
        window.location.href = '/cart';
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      this.button.classList.remove('is-loading');
      this.button.disabled = false;
    }
  }

  initVariantSelection() {
    if (!this.optionSelects || this.optionSelects.length === 0) return;

    // Initialize select dropdown handlers
    this.optionSelects.forEach((select) => {
      const optionIndex = parseInt(select.getAttribute('data-onyx-index'), 10);

      // Set initial value
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption) {
        this.state.options[optionIndex] = selectedOption.getAttribute('data-onyx-value-handle') || selectedOption.value;
      }

      // Handle select changes
      select.addEventListener('change', (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex];
        if (selectedOption) {
          this.state.options[optionIndex] = selectedOption.getAttribute('data-onyx-value-handle') || selectedOption.value;
          this.updateSelection();
        }
      });
    });

    this.updateSelection();
  }

  updateSelection() {
    if (this.productData && this.productData.product) {
      const matchingVariant = this.productData.product.variants.find(variant => {
        return variant.options.every((optionValue, index) => {
          const selectedHandle = this.state.options[index];
          if (!selectedHandle || !optionValue) return false;
          const optionHandle = optionValue.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          return optionHandle === selectedHandle;
        });
      });

      if (matchingVariant) {
        this.state.id = matchingVariant.id.toString();
        this.updateVariant(this.state.id);
        return;
      }
    }

    // Fallback: Match variant ID with handleized option string from master select
    if (this.mainVariantSelect) {
      const variantKey = this.state.options.filter(Boolean).join('-');
      for (let option of this.mainVariantSelect.options) {
        if (option.text === variantKey) {
          this.state.id = option.value;
          this.updateVariant(this.state.id);
          break;
        }
      }
    }
  }

  updateVariant(variantId) {
    if (!variantId) return;

    // Update the hidden select element
    if (this.mainVariantSelect) {
      this.mainVariantSelect.value = variantId;
    }

    // Update price
    this.updatePrice();

    // Update availability
    this.updateAvailability();

    // Update stock badge
    this.updateStockBadge();
  }

  updateStockBadge() {
    if (!this.mainVariantSelect || !this.stockBadge) return;

    const selectedOption = this.mainVariantSelect.options[this.mainVariantSelect.selectedIndex];
    if (!selectedOption) return;

    try {
      const locationData = JSON.parse(selectedOption.getAttribute('data-location-based-quantity') || '{}');
      const restrictedWarehouse = this.dataset.restrictedWarehouse;
      const isAvailable = selectedOption.getAttribute('data-variant-available') === 'true';
      let inventoryCount = 0;

      if (restrictedWarehouse && typeof locationData[restrictedWarehouse] !== 'undefined') {
        inventoryCount = locationData[restrictedWarehouse];
      } else {
        // Fallback to first available location if no restricted warehouse
        for (let key in locationData) {
          inventoryCount = locationData[key];
          break;
        }
      }

      if (isAvailable && inventoryCount > 0) {
        this.stockBadge.textContent = `Only ${inventoryCount} boxes left`;
        this.stockBadge.style.display = 'inline-block';
      } else {
        this.stockBadge.style.display = 'none';
      }
    } catch (e) {
      console.error('Error updating stock badge:', e);
      this.stockBadge.style.display = 'none';
    }
  }

  updatePrice() {
    if (!this.mainVariantSelect || !this.priceElement) return;

    const selectedOption = this.mainVariantSelect.options[this.mainVariantSelect.selectedIndex];
    if (!selectedOption) return;

    const price = selectedOption.getAttribute('data-variant-price');
    const comparePrice = selectedOption.getAttribute('data-variant-compare');
    const priceContainer = this.priceElement.querySelector('.price');

    if (priceContainer && price) {
      const isOnSale = comparePrice && parseFloat(comparePrice.replace(/[^0-9.]/g, '')) > parseFloat(price.replace(/[^0-9.]/g, ''));

      priceContainer.className = 'price price--large';
      if (isOnSale) {
        priceContainer.classList.add('price--on-sale');
        priceContainer.innerHTML = `
          <div class="price__sale">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <s class="price-item price-item--regular">
              <span>${comparePrice}</span>
            </s>
            <span class="visually-hidden visually-hidden--inline">Sale price</span>
            <span class="price-item price-item--sale price-item--last">
              <span>${price}</span>
            </span>
          </div>
        `;
      } else {
        priceContainer.innerHTML = `
          <div class="price__regular">
            <span class="visually-hidden visually-hidden--inline">Regular price</span>
            <span class="price-item price-item--regular">
              <span>${price}</span>
            </span>
          </div>
        `;
      }
    }
  }

  updateAvailability() {
    if (!this.button || !this.mainVariantSelect) return;

    const selectedOption = this.mainVariantSelect.options[this.mainVariantSelect.selectedIndex];
    if (!selectedOption) return;

    const isAvailable = selectedOption.getAttribute('data-variant-available') === 'true';

    const buttonText = this.button.querySelector('.key-points-onyx__atc-button-text');
    if (buttonText) {
      if (isAvailable) {
        this.button.disabled = false;
        buttonText.textContent = window.variantStrings?.addToCart || 'Shop The Coffee';
        if (this.preorderLabel) {
          this.preorderLabel.style.display = 'block';
        }
      } else {
        this.button.disabled = true;
        buttonText.textContent = window.variantStrings?.soldOut || 'SOLD OUT';
        if (this.preorderLabel) {
          this.preorderLabel.style.display = 'none';
        }
      }
    }
  }

  updatePreorderDate() {
    if (!this.preorderLabel) return;

    // Default cutoff settings (America/Chicago timezone)
    const targetWeekday = 4; // Thursday
    const cutoffWeekday = 3; // Wednesday
    const cutoffHour = 17;   // 5:00 PM CDT/CST

    // Get the current local time in Chicago timezone
    const chicagoDateParts = this.getChicagoTimeParts();
    if (!chicagoDateParts) return;

    const { year, month, day, hour, minute, second } = chicagoDateParts;

    // Create a local Date representation based on Chicago time parts
    const localDate = new Date(year, month, day, hour, minute, second);
    const dayOfWeek = localDate.getDay();
    const hourOfDay = localDate.getHours();

    // Calculate days to add to get to the shipping Thursday
    let daysToAdd = (targetWeekday - dayOfWeek + 7) % 7;
    if (daysToAdd === 0 || (dayOfWeek === cutoffWeekday && hourOfDay >= cutoffHour)) {
      daysToAdd += 7;
    }

    // Add the days to get the shipping date
    const shippingDate = new Date(localDate);
    shippingDate.setDate(localDate.getDate() + daysToAdd);

    // Format the date: "This preorder ships June 4th"
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const shippingMonth = monthNames[shippingDate.getMonth()];
    const shippingDay = shippingDate.getDate();

    this.preorderLabel.textContent = `This preorder ships ${shippingMonth}. ${shippingDay}`;
  }

  getChicagoTimeParts() {
    try {
      const options = {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
      };
      const formatter = new Intl.DateTimeFormat('en-US', options);
      const parts = formatter.formatToParts(new Date());

      const partValues = {};
      for (const part of parts) {
        partValues[part.type] = part.value;
      }

      return {
        year: parseInt(partValues.year, 10),
        month: parseInt(partValues.month, 10) - 1, // 0-indexed
        day: parseInt(partValues.day, 10),
        hour: parseInt(partValues.hour, 10),
        minute: parseInt(partValues.minute, 10),
        second: parseInt(partValues.second, 10)
      };
    } catch (e) {
      console.error('Error getting America/Chicago timezone parts:', e);
      return null;
    }
  }
}

if (!customElements.get('key-points-onyx')) {
  customElements.define('key-points-onyx', KeyPointsOnyx);
}
